import { useApolloClient } from '@apollo/client';
import Decimal from 'decimal.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';

import {
  CompoundSelectInputOption,
  TypeaheadSelectInputOption,
} from '@/components/form/baseInputs/inputTypes';
import { LoadingAdornment } from '@/components/form/baseInputs/SelectInput/LoadingAdornment';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { useFormContext } from '@/components/react-hook-form/hooks';
import { getKindFromProvider } from '@/modules/assetProviderIntegrations/assetProviderIntegrations.utils';
import {
  AssetIntegrationProviders,
  getIntegrationProviderDisplayName,
} from '@/modules/assetProviderIntegrations/shared/constants';
import { useIsAssetProviderIntegrationEnabled } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import {
  getInferNameMatchCandidatesFromOptions,
  inferInsurancePolicyKind,
  inferNameMatch,
} from '@/modules/assetProviderIntegrations/shared/integrationInference';
import { IntegrationInputCard } from '@/modules/assetProviderIntegrations/shared/IntegrationInputCard';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';

import { IntegrationInsuranceSelect_IntegrationInsuranceFragment } from '../graphql/IntegrationInsuranceSelect.generated';
import { useGetIntegrationInsurancesQuery } from '../graphql/IntegrationInsuranceSelect.generated';
import { getInsurancePolicyIntegrationPrefilledFields } from '../InsurancePolicyDetailsSubform.integration';
import {
  defaultPolicy,
  Fields,
  INSURANCE_POLICIES_DETAILS_SUBFORM,
  InsurancePolicyDetails,
  InsurancePolicyDetailsSubformTypePaths,
} from '../InsurancePolicyDetailsSubform.types';

const MAX_OPTIONS = 50;

/**
 * Given a selected integration insurance node, prefill the corresponding form fields.
 * Fields that are absent on the integration item are reset to their defaults so that
 * switching between items doesn't leave stale values from a previous selection.
 */
function prefillFromIntegration(
  node: IntegrationInsuranceSelect_IntegrationInsuranceFragment,
  policyIndex: number,
  setValue: ReturnType<typeof useFormContext<Fields>>['setValue']
) {
  const prefix =
    `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies.${policyIndex}` as const;

  const set = <K extends keyof typeof defaultPolicy>(
    field: K,
    value: (typeof defaultPolicy)[K]
  ) => {
    setValue(
      `${prefix}.${field}` as InsurancePolicyDetailsSubformTypePaths,
      value as never
    );
  };

  set(
    'deathBenefitAmount',
    node.value != null
      ? new Decimal(node.value)
      : defaultPolicy.deathBenefitAmount
  );
  const cashValue =
    node.cashValue != null
      ? new Decimal(node.cashValue)
      : defaultPolicy.cashValue;
  set('cashValue', cashValue);
  set('cashValueDate', cashValue ? new Date() : defaultPolicy.cashValueDate);
  set('policyNumber', node.policyNumber ?? defaultPolicy.policyNumber);
  set('policyType', defaultPolicy.policyType);
  set(
    'premiumAmount',
    node.premiumAmount != null
      ? new Decimal(node.premiumAmount)
      : defaultPolicy.premiumAmount
  );
  set(
    'policyStartDate',
    node.startDate ? new Date(node.startDate) : defaultPolicy.policyStartDate
  );
  set('termLength', node.termDurationYears ?? defaultPolicy.termLength);
  set('policyHolderIDs', [...defaultPolicy.policyHolderIDs]);
}

interface IntegrationInsuranceSelectProps {
  policyIndex: number;
  provider: AssetIntegrationProviders;
  onPrefill?: (fields: Set<keyof InsurancePolicyDetails>) => void;
  policyHolderOptions: CompoundSelectInputOption<string>[];
}

export function IntegrationInsuranceSelect({
  policyIndex,
  provider,
  onPrefill,
  policyHolderOptions,
}: IntegrationInsuranceSelectProps) {
  const apolloClient = useApolloClient();
  const integrationInsuranceIdFieldName =
    `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies.${policyIndex}.integrationInsuranceId` as const satisfies InsurancePolicyDetailsSubformTypePaths;

  const { control, getFieldState, getValues, setValue } =
    useFormContext<Fields>();
  const { householdId } = useHouseholdDetailsContext();
  const { integrationsConnected } = useIsAssetProviderIntegrationEnabled();
  const [isInferring, setIsInferring] = useState(false);
  const [inferredPrefilledFields, setInferredPrefilledFields] = useState<
    Set<keyof InsurancePolicyDetails>
  >(new Set());

  const integrationClientKind = getKindFromProvider(provider);
  const providerName = getIntegrationProviderDisplayName(provider);
  const isLoggedIn = integrationsConnected.includes(provider);

  const selectedId = useWatch({
    control,
    name: integrationInsuranceIdFieldName,
  });

  const { data, previousData } = useGetIntegrationInsurancesQuery({
    variables: {
      optionsLike: {
        hasIntegrationClientWith: [
          {
            kind: integrationClientKind,
            hasClientHouseholdsWith: [{ id: householdId! }],
          },
        ],
      },
      selectedOptionsLike: { idIn: selectedId ? [selectedId] : [''] },
      first: MAX_OPTIONS,
    },
    skip: !householdId,
  });

  const currentData = data ?? previousData;

  const allNodes = useMemo(() => {
    const queryNodes =
      currentData?.queryData?.edges
        ?.map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => n != null) ?? [];
    const selectedNodes =
      currentData?.selectedData?.edges
        ?.map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => n != null) ?? [];

    const merged = [...queryNodes];
    for (const selected of selectedNodes) {
      if (!merged.some((n) => n.id === selected.id)) {
        merged.push(selected);
      }
    }
    return merged;
  }, [currentData]);

  const options: TypeaheadSelectInputOption<string>[] = useMemo(() => {
    return allNodes.map((node) => {
      const valueLabel =
        node.value != null
          ? ` (${formatCurrencyNoDecimalsAccounting(new Decimal(node.value))})`
          : '';

      return {
        value: node.id,
        display: `${node.name}${valueLabel}`,
        groupName: node.integrationClient?.name ?? providerName,
      };
    });
  }, [allNodes, providerName]);

  const selectedInsurance = useMemo(
    () => allNodes.find((node) => node.id === selectedId),
    [allNodes, selectedId]
  );
  const policyHolderCandidates = useMemo(
    () => getInferNameMatchCandidatesFromOptions(policyHolderOptions),
    [policyHolderOptions]
  );

  const basePrefilledFields = useMemo(() => {
    if (!selectedInsurance) return null;

    return getInsurancePolicyIntegrationPrefilledFields({
      deathBenefitAmount: selectedInsurance.value,
      cashValue: selectedInsurance.cashValue,
      policyNumber: selectedInsurance.policyNumber ?? '',
      policyType: '',
      premiumAmount: selectedInsurance.premiumAmount,
      policyStartDate: selectedInsurance.startDate,
      termLength: selectedInsurance.termDurationYears ?? '',
    });
  }, [selectedInsurance]);
  const selectedInsurancePrefilledFields = useMemo(() => {
    if (!basePrefilledFields) return null;
    return new Set([...basePrefilledFields, ...inferredPrefilledFields]);
  }, [basePrefilledFields, inferredPrefilledFields]);

  // Keep the disabled-field state in sync with the selected integration node,
  // including when an edit form loads with an existing linked policy.
  useEffect(() => {
    if (!selectedInsurancePrefilledFields) return;

    onPrefill?.(selectedInsurancePrefilledFields);
  }, [selectedInsurancePrefilledFields, onPrefill]);

  // Prefill form fields when the linked integration selection changes, not on mount.
  const prevSelectedIdRef = useRef(selectedId);
  useEffect(() => {
    if (!selectedInsurance) return;
    if (selectedId === prevSelectedIdRef.current) return;

    prevSelectedIdRef.current = selectedId;
    setInferredPrefilledFields(new Set());

    prefillFromIntegration(selectedInsurance, policyIndex, setValue);
  }, [policyIndex, selectedId, selectedInsurance, setValue]);

  useEffect(() => {
    if (!selectedInsurance || selectedId !== prevSelectedIdRef.current) return;

    const currentSelectedId = selectedId;
    const policyTypeName =
      `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies.${policyIndex}.policyType` as const;
    const policyHolderName =
      `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies.${policyIndex}.policyHolderIDs.0` as const;
    const canSet = (name: typeof policyTypeName | typeof policyHolderName) =>
      !getValues(name) && !getFieldState(name).isDirty;
    if (!canSet(policyTypeName) && !canSet(policyHolderName)) return;

    let isStale = false;
    const infer = async () => {
      setIsInferring(true);
      try {
        const [policyType, policyHolderId] = await Promise.all([
          canSet(policyTypeName)
            ? inferInsurancePolicyKind({
                client: apolloClient,
                input: selectedInsurance.policyType,
              })
            : null,
          canSet(policyHolderName)
            ? inferNameMatch({
                client: apolloClient,
                name: selectedInsurance.policyHolderName,
                candidates: policyHolderCandidates,
              })
            : null,
        ]);
        if (
          isStale ||
          getValues(integrationInsuranceIdFieldName) !== currentSelectedId
        ) {
          return;
        }
        if (policyType && canSet(policyTypeName)) {
          setValue(policyTypeName, policyType);
          setInferredPrefilledFields((fields) =>
            new Set(fields).add('policyType')
          );
        }
        if (policyHolderId && canSet(policyHolderName)) {
          setValue(policyHolderName, policyHolderId);
          setInferredPrefilledFields((fields) =>
            new Set(fields).add('policyHolderIDs')
          );
        }
      } finally {
        if (!isStale) setIsInferring(false);
      }
    };
    void infer();
    return () => {
      isStale = true;
    };
  }, [
    apolloClient,
    getFieldState,
    getValues,
    integrationInsuranceIdFieldName,
    policyHolderCandidates,
    policyIndex,
    selectedId,
    selectedInsurance,
    setValue,
  ]);

  return (
    <IntegrationInputCard providerName={providerName} variant="filled">
      <FormAwareTypeaheadSelectInput<Fields>
        control={control}
        label={`${providerName} life insurance`}
        hideLabel
        fieldName={integrationInsuranceIdFieldName}
        options={options}
        groupBy={(option) => option.groupName ?? ''}
        placeholder="Select a life insurance policy to link"
        startAdornment={isInferring ? <LoadingAdornment /> : undefined}
        helpText={
          isLoggedIn
            ? undefined
            : `You have not linked your ${providerName} account. Go to manage profile under user settings to link.`
        }
        disabled={!isLoggedIn}
      />
    </IntegrationInputCard>
  );
}
